import type {
  TrainingEvalRunSummaryView,
  TrainingEvalRunView,
  TrainingEvalScenarioView,
  TrainingImageMetadataTaskView,
  TrainingSuccessCriteriaValue,
} from "@/api/trainingEval";
import type { JsonObject } from "@/api/common";

export type RunExecution = { tag: string | null; task: string | null; image: string | null };

function textValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function objectField(value: unknown, key: string): unknown {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

/** 실행 정보의 표시·조회 기준이다. 현재 scenario 는 기록된 값이 없을 때만 보완한다. */
export function resolveRunExecution(
  summary: Pick<TrainingEvalRunSummaryView, "docker_tag" | "task">,
  detail: Pick<TrainingEvalRunView, "params" | "override_params"> | null,
  scenario: TrainingEvalScenarioView | null,
): RunExecution {
  const recordedTag = textValue(summary.docker_tag);
  const tag = recordedTag ?? imageTag(scenario?.benchmark_image ?? null);
  const task =
    textValue(summary.task) ??
    textValue(objectField(detail?.params.eval_scenario_options, "TASK")) ??
    textValue(scenario?.tunable_env_vars.TASK) ??
    textValue(scenario?.fixed_env_vars.TASK);
  let image: string | null = null;
  for (const source of [detail?.override_params, detail?.params]) {
    image =
      textValue(source?.eval_image) ?? textValue(objectField(source?.eval_execution, "image"));
    if (image !== null) break;
  }
  // Tag 만으로는 실행 당시 repository 를 복원할 수 없다.
  if (image === null && recordedTag === null) image = scenario?.benchmark_image ?? null;
  if (image !== null && tag !== null && imageTag(image) !== tag) image = null;
  return { tag, task, image };
}

function preferActiveScenario(
  incumbent: TrainingEvalScenarioView | undefined,
  candidate: TrainingEvalScenarioView,
): TrainingEvalScenarioView {
  return incumbent === undefined || (incumbent.is_active !== true && candidate.is_active === true)
    ? candidate
    : incumbent;
}

export function resolveEvalScenario(
  scenarios: readonly TrainingEvalScenarioView[],
  name: string | null,
): TrainingEvalScenarioView | null {
  if (name === null) return null;
  let winner: TrainingEvalScenarioView | undefined;
  for (const scenario of scenarios) {
    if (scenario.name === name) winner = preferActiveScenario(winner, scenario);
  }
  return winner ?? null;
}

/** 이름별로 첫 활성 scenario를 고르는 조회 색인을 만든다. */
export function scenarioIndexByName(
  scenarios: readonly TrainingEvalScenarioView[],
): ReadonlyMap<string, TrainingEvalScenarioView> {
  const index = new Map<string, TrainingEvalScenarioView>();
  for (const scenario of scenarios) {
    index.set(scenario.name, preferActiveScenario(index.get(scenario.name), scenario));
  }
  return index;
}

function imageTag(image: string | null): string | null {
  if (image === null) return null;
  const lastSegment = image.split("/").at(-1) ?? image;
  const separator = lastSegment.lastIndexOf(":");
  return separator > 0 ? lastSegment.slice(separator + 1) : null;
}

function numericEnvValue(values: Record<string, string>, key: string): number | null {
  const raw = values[key];
  if (raw === undefined) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function scenarioEnvironmentCount(scenario: TrainingEvalScenarioView | null): number | null {
  if (scenario === null) return null;
  return (
    numericEnvValue(scenario.tunable_env_vars, "NUM_ENVS") ??
    numericEnvValue(scenario.fixed_env_vars, "NUM_ENVS")
  );
}

function paramByKeys(params: JsonObject | null, keys: readonly string[]): string | null {
  if (params === null) return null;
  for (const key of keys) {
    const value = params[key];
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return null;
}

// 대문자 SEED는 제출 측 시드 키 계약에 포함된다.
function runSeed(params: JsonObject | null): string | null {
  return paramByKeys(params, ["seed", "SEED", "SIMULATION_SEED", "simulation_seed"]);
}

export function simulationSeed(params: JsonObject | null): string | null {
  return paramByKeys(params, ["SIMULATION_SEED", "simulation_seed", "SEED", "seed"]);
}

export function runEpisodes(params: JsonObject | null): string | null {
  return paramByKeys(params, ["NUM_EPISODES", "EPISODES", "num_episodes", "episodes"]);
}

export function scenarioParams(scenario: TrainingEvalScenarioView | null): JsonObject | null {
  if (scenario === null) return null;
  return { ...scenario.fixed_env_vars, ...scenario.tunable_env_vars };
}

/** run params 와 scenario env 에서 Seed 로 읽는 키다. 철자가 늘면 이 목록만 고친다. */
export function isSeedKey(key: string): boolean {
  const normalized = key.toUpperCase();
  return normalized === "SEED" || normalized === "SIMULATION_SEED";
}

export function scenarioParameterRows(
  scenario: TrainingEvalScenarioView | null,
  params: JsonObject | null,
  task?: string | null,
): Array<[string, string]> {
  if (scenario === null && task === undefined) return [];
  const values = { ...scenarioParams(scenario) };
  if (task !== undefined) {
    delete values.TASK;
    if (task !== null) values.TASK = task;
  }
  const rows = Object.entries(values).map(([key, value]): [string, string] => [key, String(value)]);
  const seed = runSeed(params);
  if (seed === null) return rows;
  let replaced = false;
  const result = rows.map(([key, value]): [string, string] => {
    if (!isSeedKey(key)) return [key, value];
    replaced = true;
    return [key, seed];
  });
  if (!replaced) result.push(["SIMULATION_SEED", seed]);
  return result;
}

export type SuccessCriterionRow = {
  name: string;
  operator: string | null;
  value: TrainingSuccessCriteriaValue;
  description?: string | null;
};

export function successCriteriaRows(
  task: TrainingImageMetadataTaskView | null | undefined,
): SuccessCriterionRow[] {
  const criteria = task?.success_criteria;
  if (criteria === null || criteria === undefined) return [];
  const contracts = Object.entries(criteria.parameter_contracts);
  if (contracts.length > 0) {
    return contracts.map(([name, contract]) => ({
      name,
      operator: contract.operator,
      value: contract.value,
      description: contract.description,
    }));
  }
  return Object.entries(criteria.parameters).map(([name, value]) => ({
    name,
    operator: null,
    value,
  }));
}

export function criterionValue(row: SuccessCriterionRow): string {
  if (row.operator === null) return String(row.value);
  if (["<", ">", ">="].includes(row.operator)) return `${row.operator} ${row.value}`;
  if (row.operator === "derived") return `derived value ${row.value}`;
  if (row.operator === "selects") return `selector ${row.value}`;
  return `${row.operator} ${row.value}`;
}
